import { useSlots as _useSlots, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, withKeys as _withKeys, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tracking-debug-circle tracked-element"
}

import { onMounted, ref, watch } from 'vue';
import { useTrackedElements, UseTrackedElementsReturn, TrackedHTMLElement } from '@/composables/useTrackedElements';

import { Place } from '@wwtelescope/engine';
import { engineStore } from '@wwtelescope/engine-pinia';
import { computed } from 'vue';
interface WWTEngineStore extends ReturnType<typeof engineStore> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  $wwt?: any;
}

type Degree = number;
// type Radian = number;
type HourAngle = number;
// type Pixel = number;
interface WWTTrackedContentProps {
  containerID?: string;
  ra?: Degree;
  dec?: Degree;
  name?: string
  place?: Place; // Optional place object, if needed
  offsetRa?: Degree;
  offsetDec?: Degree;
  offsetX?: Degree; // Degree offset in the screen X direction
  offsetY?: Degree; // Degree offset in the screen Y direction
  store: WWTEngineStore;
  visible?: boolean;
  centerOnClick?: boolean;
  zoomDeg?: Degree | null; // Optional zoom level, if needed
  clickInstant?: boolean; // Optional, if you want to control the instant navigation
  doubleClickInstant?: boolean; // Optional, if you want to control the instant navigation
  goToPlace?: boolean; // Optional, if you want it to go to the place and ignore zoomDeg
  debug?: boolean; // Optional, for debugging purposes
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WWTTrackedContent',
  props: {
    containerID: { default: '' },
    ra: {},
    dec: {},
    name: { default: 'default name' },
    place: { default: undefined },
    offsetRa: {},
    offsetDec: {},
    offsetX: {},
    offsetY: {},
    store: {},
    visible: { type: Boolean, default: true },
    centerOnClick: { type: Boolean, default: false },
    zoomDeg: { default: null },
    clickInstant: { type: Boolean, default: false },
    doubleClickInstant: { type: Boolean, default: true },
    goToPlace: { type: Boolean, default: false },
    debug: { type: Boolean, default: false }
  },
  setup(__props: any) {

//  eslint-disable @typescript-eslint/no-unused-vars 
const D2R = Math.PI / 180;
// const R2D = 180 / Math.PI;
const props = __props;

const ra = ref<number>(props.ra as unknown as number);
const dec = ref<number>(props.dec as unknown as number);
const name = ref(props.name);
const zoomDeg = computed(() => {
  return props.zoomDeg !== null ? props.zoomDeg : props.store.zoomDeg;
});

if (props.place) {
  const iset = props.place.get_backgroundImageset() ?? props.place.get_studyImageset();
  
  if (iset === null) {
    console.warn(`Place ${props.place.get_name()} does not have a background or study imageset.`);
  } else {
    ra.value = (props.place.get_RA() as HourAngle) * 15 as Degree;
    dec.value = props.place.get_dec();
    name.value = props.name ?? iset.get_name();
  }
}


const ready = ref(false);
const parent = ref<HTMLElement | null>(null);
const trackedElement = ref<TrackedHTMLElement | null>(null);

function goTo(instant=false) {
  if (ready.value && trackedElement.value !== null) {
    console.log("Current zoomDeg", zoomDeg.value);
    console.log("Current wwt zoomDeg", props.store.zoomDeg);
    if (props.place && props.goToPlace) {
      props.store.gotoTarget({
        place: props.place,
        instant,
        noZoom: false,
        trackObject: true,
      });
      
    } else if (ra.value && dec.value) {
      props.store.gotoRADecZoom({
        raRad: ra.value * (D2R), 
        decRad: dec.value * (D2R), 
        zoomDeg: zoomDeg.value,
        instant: false,
      });
    }
    
  }
}

function onClick() {
  if (props.centerOnClick && ready.value && trackedElement.value !== null) {
    goTo(props.clickInstant);
  }
}

function onDoubleClick() {
  if (props.centerOnClick && ready.value && trackedElement.value !== null) {
    goTo(props.doubleClickInstant);
  }
}

const ute = ref<UseTrackedElementsReturn | null>(null);

const _rot = computed(() => {
  return {'sin': Math.sin(props.store.rollRad), 'cos': Math.cos(props.store.rollRad)};
});

if (props.offsetX !== undefined && props.offsetY !== undefined) {
  console.error('While supported, I am not sure the he simple 2D rotation transforms for offsetX and offsetY are correct.');
}


// This will get
const offsetRA = ref<Degree>(props.offsetRa ?? 0);
const offsetDec = ref<Degree>(props.offsetDec ?? 0);

if (props.offsetX !== undefined && props.offsetY !== undefined ) {
  // throw and error and return if offsetX and offsetY are provided
  console.error('offsetX and offsetY ARE not setup. Rotate the coordinates before passing them to the component.');
  throw new Error('offsetX and offsetY are not supported yet. Please use offsetRa and offsetDec instead.');
}


// If we want it maintain the same orientation relative to the Screen we need to use this
// const offsetRA = computed(() => {
//   if (props.offsetX !== undefined && props.offsetY !== undefined ) {
//     return (props.offsetX)  * rot.value.cos + props.offsetY * rot.value.sin;
//   }
//   return props.offsetRa ?? 0;
// });

// const offsetDec = computed(() => {
//   if (props.offsetX !== undefined && props.offsetY !== undefined ) {
//     return -(props.offsetX) * rot.value.sin + props.offsetY * rot.value.cos;
//   }
//   return props.offsetDec ?? 0;
// });

onMounted(() => {
  props.store.waitForReady().then(() => {

    
    ute.value = useTrackedElements(props.containerID, props.store) as unknown as typeof ute.value;
    if (!ute.value) {
      console.error('useTrackedElements returned null');
      return;
    }
    trackedElement.value = ute.value.placeElement(
      parent.value as HTMLElement | null,
      {
        ra: ra.value + offsetRA.value,
        dec: dec.value + offsetDec.value
      },
      name.value
    );
    updateVisibility(props.visible);
  })
    .then(() => {
      ready.value = true;
    })
    .catch((error) => {
      console.error(error);
    });
});

function updateVisibility(visible: boolean) {
  const element = trackedElement.value;
  if (element) {
    element.style.visibility = visible ? "visible" : "hidden";
  }
}

watch(() => props.visible, updateVisibility);

watch(() => [props.ra, props.dec, offsetRA.value, offsetDec.value], ([newRa, newDec, newOffsetRA, newOffsetDec]) => {
  if (!trackedElement.value) return;
  trackedElement.value.move({
    ra: (newRa ?? ra.value) + (newOffsetRA ?? offsetRA.value),
    dec: (newDec ?? dec.value) + (newOffsetDec ?? offsetDec.value)
  });
  if (ready.value && ute.value) {
    ute.value.updateElements();
  }
}, { immediate: true });


// eslint-disable-next-line @typescript-eslint/no-unused-vars
const slots = _useSlots();



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "wwt-tracked-content",
    ref_key: "parent",
    ref: parent,
    tabindex: "0"
  }, [
    _renderSlot(_ctx.$slots, "default", {
      on: {'click': onClick, 'onDoubleClick': onDoubleClick, 'keydown.enter': onClick},
      ra: ra.value,
      dec: dec.value,
      _name: name.value,
      offsetDec: offsetDec.value,
      offsetRa: offsetRA.value
    }, () => [
      _createElementVNode("div", {
        class: _normalizeClass(['default-wwt-tracked-content-content', { debug: __props.debug }]),
        onClick: onClick,
        onDblclick: onDoubleClick,
        onKeydown: _withKeys(onClick, ["enter"])
      }, null, 34)
    ]),
    (__props.debug)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1))
      : _createCommentVNode("", true)
  ], 512))
}
}

})